/**
 * External dependencies
 */
import { Button as AriakitButton } from '@ariakit/react';
import { forwardRef } from '@wordpress/element';
import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import type { ButtonProps as AriakitButtonProps } from '@ariakit/react';

/**
 * Internal dependencies
 */
import './styles.scss';

type WCButtonProps = AriakitButtonProps & { children?: React.ReactNode };

export interface ButtonProps extends WCButtonProps {
	/**
	 * Button variant
	 *
	 * @default 'contained'
	 */
	variant?: 'text' | 'contained' | 'outlined';
	/**
	 * By default we render a wrapper around  the button children,
	 * but you can opt in to removing it by setting removeTextWrap
	 * to true.
	 *
	 * @default false
	 */
	removeTextWrap?: boolean;
	/**
	 * Render the button as a different element (e.g., 'span' for non-interactive display).
	 * Useful for status indicators that need button styling without button semantics.
	 *
	 * @default undefined (renders as button)
	 */
	renderAs?: 'span';
}

interface LinkProps extends ButtonProps {
	/**
	 * Button href
	 */
	href: string;
}

/**
 * Component that visually renders a button but semantically might be `<button>` or `<a>` depending
 * on the props.
 */
const Button = forwardRef< HTMLButtonElement, ButtonProps | LinkProps >(
	( props, ref ) => {
		const {
			className,
			children,
			variant = 'contained',
			// To maintain backward compat we render a wrapper for button text by default,
			// but you can opt in to removing it by setting removeTextWrap to true.
			removeTextWrap = false,
			renderAs,
			...rest
		} = props;

		const buttonClassName = clsx(
			'leat-block-components-button',
			'wp-element-button wp-block-button__link',
			// Add wp-block-button__link for theme compatibility (makes buttons inherit theme colors)
			variant === 'contained' && 'wp-block-button__link',
			className,
			variant,
			renderAs && 'is-display-only'
		);

		const buttonChildren = removeTextWrap ? (
			props.children
		) : (
			<div className="leat-block-components-button__text">
				{ props.children }
			</div>
		);

		// Render as span for non-interactive display
		if ( renderAs === 'span' ) {
			return (
				<span
					ref={ ref as ForwardedRef< HTMLSpanElement > }
					className={ buttonClassName }
					{ ...rest }
				>
					{ buttonChildren }
				</span>
			);
		}

		// Render as link
		if ( 'href' in props ) {
			return (
				<AriakitButton
					render={
						<a
							ref={ ref as ForwardedRef< HTMLAnchorElement > }
							href={ props.href }
						>
							<div className="leat-block-components-button__text">
								{ children }
							</div>
						</a>
					}
					className={ buttonClassName }
					{ ...rest }
				/>
			);
		}

		// Render as button (default)
		return (
			<AriakitButton
				ref={ ref }
				className={ buttonClassName }
				{ ...rest }
			>
				{ buttonChildren }
			</AriakitButton>
		);
	}
);

export default Button;